// src/utils/accueil.ts — SCE Issues
// Accueil V2 (Lot 9) : ce que l'accueil montre, calculé sans réseau ni React.
//
// ─── Coordinateur : quatre files ───
//
//   À qualifier            actif, famille ou criticité « À qualifier »
//   Critiques en cours     actif, criticité Critique
//   Sans activité depuis 7 j   « En cours » dont le DERNIER événement date
//                          de plus de 7 jours (arbitrage du 25/09)
//   En cours               statut En cours
//
// Toutes limitées aux métiers du coordinateur.
//
// « Sans activité » ne compte que les problèmes En cours : un problème ouvert
// jamais qualifié est déjà dans « À qualifier », avec son âge en rouge au-delà
// de 7 jours. Le compter deux fois ferait gonfler les deux chiffres.
//
// ─── Dernière activité d'un problème ───
//
// La date du plus récent de ses événements (tous types : commentaire,
// qualification, modification, clôture, réouverture…), sinon sa date de
// déclaration. Ouvrir une fiche n'en est pas une.

import {
  CRITICITES_ORDRE,
  cleRapprochement,
  compteCommeProbleme,
  estActif,
  type Commentaire,
  type Issue,
} from '@/types/issue.types'
import { estAQualifierActif, estCritiqueActif } from '@/utils/dashboardAggregations'
import { METIERS_LABELS_COURT, METIERS_ORDRE } from '@/config/metiers.config'
import { dansPerimetre, type ClePerimetre } from '@/config/sujets.config'
import type { DateEvenement } from '@/api/commentaires.api'
import { libelleMachines, sansRenvoiGroupe, unParGroupe } from '@/utils/groupes'
import { LIBELLE_EVENEMENT_NCS, lireEvenementNcs } from '@/utils/declarationNcs'
import { lireEvenementEnvoi, resumeEvenementEnvoi } from '@/utils/envoiBE'
import { natureAutomatique } from '@/utils/rattachementPhotos'

export const JOURS_SANS_ACTIVITE = 7
/** Jours sans passer sur l'accueil qui font un retour d'absence (28/10, utils/retourAbsence). */
export const SEUIL_ABSENCE_JOURS = 5
const JOUR_MS = 24 * 3600 * 1000

// ─── Temps ───

function ms(iso: string | undefined): number {
  const t = iso ? Date.parse(iso) : Number.NaN
  return Number.isNaN(t) ? 0 : t
}

/** Jours pleins écoulés. */
export function joursDepuis(iso: string | undefined, maintenant: number): number {
  const t = ms(iso)
  return t ? Math.floor((maintenant - t) / JOUR_MS) : 0
}

/** « à l'instant », « 12 min », « 3 h », « 5 j », « 2 sem. », « 3 mois ». */
/**
 * Salutation de l'accueil selon le moment (28/09), heure locale du poste.
 * Première règle qui s'applique :
 *   lundi avant 12 h          → « Bon début de semaine, Prénom »
 *   vendredi à partir de 14 h → « Bonne fin de semaine, Prénom »
 *   18 h – 5 h                → « Bonsoir Prénom »
 *   retour après une pause    → « Re-bonjour Prénom »
 *   sinon                     → « Bonjour Prénom »
 * La visite précédente est réécrite à CHAQUE passage sur l'accueil : un
 * aller-retour par une fiche (moins de 30 min) n'est pas un retour de pause,
 * et au-delà de 3 h ce n'est plus la même demi-journée.
 *
 * Avant tout (28/10) : « Bon retour, Prénom » après une absence d'au moins
 * SEUIL_ABSENCE_JOURS — et toute la journée du retour (`retour`, noté dans le
 * bloc retourAbsence de Prefs), la visite précédente ayant avancé entre-temps.
 */
export function salutation(prenom: string, maintenant: number, visitePrecedente: string | null, retour: string | null = null): string {
  const d = new Date(maintenant)
  const heure = d.getHours()
  const jour = d.getDay() // 0 = dimanche
  const avecVirgule = (s: string): string => (prenom ? `${s}, ${prenom}` : s)
  const sansVirgule = (s: string): string => (prenom ? `${s} ${prenom}` : s)

  const precedenteMs = ms(visitePrecedente ?? undefined)
  const absent = precedenteMs > 0 && maintenant - precedenteMs >= SEUIL_ABSENCE_JOURS * JOUR_MS
  const jourDuRetour = ms(retour ?? undefined) > 0 && new Date(ms(retour ?? undefined)).toDateString() === d.toDateString()
  if (absent || jourDuRetour) return avecVirgule('Bon retour')
  if (jour === 1 && heure >= 5 && heure < 12) return avecVirgule('Bon début de semaine')
  if (jour === 5 && heure >= 14) return avecVirgule('Bonne fin de semaine')
  if (heure >= 18 || heure < 5) return sansVirgule('Bonsoir')
  const precedente = ms(visitePrecedente ?? undefined)
  const ecart = precedente ? maintenant - precedente : 0
  if (ecart >= 30 * 60 * 1000 && ecart < 3 * 3600 * 1000) return sansVirgule('Re-bonjour')
  return sansVirgule('Bonjour')
}

export function libelleAge(iso: string | undefined, maintenant: number): string {
  const t = ms(iso)
  if (!t) return ''
  const min = Math.max(0, Math.floor((maintenant - t) / 60000))
  if (min < 1) return 'à l\'instant'
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} h`
  const j = Math.floor(h / 24)
  if (j < 14) return `${j} j`
  if (j < 60) return `${Math.floor(j / 7)} sem.`
  return `${Math.floor(j / 30)} mois`
}

// ─── Dernière activité ───

/**
 * Dernière activité (ms) de chaque problème : son plus récent événement, ou
 * sa déclaration. Les identifiants sont comparés tels quels : c'est l'API qui
 * les écrit, au format P2026-0042.
 */
export function dernieresActivites(
  issues: readonly Issue[],
  dates: readonly DateEvenement[],
): Map<string, number> {
  const res = new Map<string, number>()
  for (const i of issues) res.set(i.id, ms(i.dateDeclaration))
  for (const d of dates) {
    const actuel = res.get(d.problemId)
    // Événement d'un problème absent de la liste (fournisseur, supprimé) : ignoré.
    if (actuel === undefined) continue
    const t = ms(d.date)
    if (t > actuel) res.set(d.problemId, t)
  }
  return res
}

// ─── Files du coordinateur ───

export type IdFile = 'aQualifier' | 'critiques' | 'sansActivite' | 'enCours'

export const ORDRE_FILES: readonly IdFile[] = ['aQualifier', 'critiques', 'sansActivite', 'enCours']

export interface FileCoordinateur {
  id: IdFile
  libelle: string
  /** Libellé court, onglets du téléphone. */
  court: string
  issues: Issue[]
  /**
   * Multi-équipements (Lot 14) : pour la file « À qualifier », un groupe tient
   * sur UNE ligne — son représentant — et ce nombre dit combien de machines
   * elle couvre. Absent = une seule.
   */
  machines?: ReadonlyMap<string, number>
  /** Une ligne sous le chiffre. */
  precision: string
  /**
   * La plus longue attente de la file, en jours (seuil d'alerte, chantier du
   * 03/11) : depuis la déclaration (à qualifier, critiques), depuis le dernier
   * événement (sans activité, en cours). 0 pour une file vide.
   */
  attenteJours: number
  /** Titre et lignes de l'infobulle (bureau). */
  infobulle: { titre: string; lignes: string[] }
}

function parMetier(issues: readonly Issue[]): string {
  return METIERS_ORDRE
    .map((m) => ({ m, n: issues.filter((i) => i.metier === m).length }))
    .filter((x) => x.n > 0)
    .map((x) => `${METIERS_LABELS_COURT[x.m]} ${x.n}`)
    .join(' · ')
}

function ligne(i: Issue, suffixe: string): string {
  return `${i.id} · ${i.titre}${suffixe ? ` — ${suffixe}` : ''}`
}

/** « 2601-H + 2 machines » : le N° de série d'une ligne qui couvre un groupe. */
export function machinesDeLaLigne(i: Issue, machines: ReadonlyMap<string, number> | undefined): string {
  const n = machines?.get(i.id) ?? 1
  return n > 1 ? `${i.numeroSerie} + ${libelleMachines(n - 1)}` : i.numeroSerie
}

const MAX_LIGNES_INFOBULLE = 5

function lignesInfobulle(issues: readonly Issue[], suffixe: (i: Issue) => string): string[] {
  const lignes = issues.slice(0, MAX_LIGNES_INFOBULLE).map((i) => ligne(i, suffixe(i)))
  if (issues.length > MAX_LIGNES_INFOBULLE) lignes.push(`… et ${issues.length - MAX_LIGNES_INFOBULLE} autre(s)`)
  return lignes
}

/**
 * Les quatre files, triées pour le travail : la plus ancienne attente d'abord.
 *
 * @param metiers les métiers du coordinateur, et les sujets dont il est
 *                responsable (23/10, Goulotte) ; vide = tous (administrateur).
 * @param delai   jours sans activité de la 3e file (réglable : 3, 7 ou 14, chantier du 03/11)
 */
export function filesCoordinateur(
  issues: readonly Issue[],
  metiers: readonly ClePerimetre[],
  activites: ReadonlyMap<string, number>,
  maintenant: number,
  delai: number = JOURS_SANS_ACTIVITE,
): FileCoordinateur[] {
  const perimetre = issues.filter((i) => dansPerimetre(i, metiers))
  const parDeclaration = (a: Issue, b: Issue): number => ms(a.dateDeclaration) - ms(b.dateDeclaration)
  const derniere = (i: Issue): number => activites.get(i.id) ?? ms(i.dateDeclaration)
  const silence = (i: Issue): number => Math.floor((maintenant - derniere(i)) / JOUR_MS)

  // Un défaut sur 3 machines se qualifie en une fois (case « appliquer au
  // groupe ») : il attend sur une ligne, pas trois.
  const { issues: aQualifier, machines } = unParGroupe(perimetre.filter(estAQualifierActif).sort(parDeclaration))
  const critiques = perimetre.filter(estCritiqueActif).sort(parDeclaration)
  const enCours = perimetre
    .filter((i) => i.statut === 'En cours')
    .sort((a, b) => derniere(a) - derniere(b))
  const sansActivite = enCours.filter((i) => silence(i) >= delai)
  const depuisDeclaration = (l: readonly Issue[]): number => Math.max(0, ...l.map((i) => joursDepuis(i.dateDeclaration, maintenant)))
  const depuisActivite = (l: readonly Issue[]): number => Math.max(0, ...l.map(silence))

  const vieux = aQualifier.filter((i) => joursDepuis(i.dateDeclaration, maintenant) >= JOURS_SANS_ACTIVITE).length
  const parCriticite = CRITICITES_ORDRE
    .map((c) => ({ c, n: enCours.filter((i) => i.criticite === c).length }))
    .filter((x) => x.n > 0)
    .map((x) => `${x.c} ${x.n}`)
    .join(' · ')

  return [
    {
      id: 'aQualifier',
      libelle: 'À qualifier',
      court: 'À qualifier',
      issues: aQualifier,
      machines,
      precision: vieux > 0
        ? `dont ${vieux} depuis plus de ${JOURS_SANS_ACTIVITE} jours`
        : (parMetier(aQualifier) || 'rien en attente'),
      attenteJours: depuisDeclaration(aQualifier),
      infobulle: {
        titre: `${aQualifier.length} problème(s) à qualifier${parMetier(aQualifier) ? ` · ${parMetier(aQualifier)}` : ''}`,
        lignes: lignesInfobulle(aQualifier, (i) => libelleAge(i.dateDeclaration, maintenant)),
      },
    },
    {
      id: 'critiques',
      libelle: 'Critiques en cours',
      court: 'Critiques',
      issues: critiques,
      precision: parMetier(critiques) || 'aucun',
      attenteJours: depuisDeclaration(critiques),
      infobulle: {
        titre: `${critiques.length} critique(s) pas encore clos`,
        lignes: lignesInfobulle(critiques, (i) => i.statut),
      },
    },
    {
      id: 'sansActivite',
      libelle: `Sans activité depuis ${delai} j`,
      court: `Sans activité ${delai} j`,
      issues: [...sansActivite],
      precision: sansActivite[0] ? `le plus ancien : ${silence(sansActivite[0])} jours` : 'aucun',
      attenteJours: depuisActivite(sansActivite),
      infobulle: {
        titre: `${sansActivite.length} problème(s) en cours sans activité depuis ${delai} jours`,
        lignes: [
          ...lignesInfobulle(sansActivite, (i) => `${silence(i)} j`),
          'Aucun commentaire, qualification, modification ni clôture depuis.',
        ],
      },
    },
    {
      id: 'enCours',
      libelle: 'En cours',
      court: 'En cours',
      issues: enCours,
      precision: parCriticite || 'aucun',
      attenteJours: depuisActivite(enCours),
      infobulle: {
        titre: `${enCours.length} problème(s) en cours`,
        lignes: parCriticite ? [parCriticite, 'Triés du plus longtemps silencieux au plus récent.'] : [],
      },
    },
  ]
}

// ─── Activité ───

export interface EvenementAccueil {
  evenement: Commentaire
  issue: Issue
  /** Postérieur à la visite précédente de l'accueil. */
  nouveau: boolean
}

/**
 * Les événements rattachés à un problème visible, les plus récents d'abord.
 * Un événement d'un problème absent de `issues` est écarté : c'est ce qui
 * cloisonne un fournisseur, dont la liste ne contient que ses déclarations.
 *
 * @param metiers métiers et sujets ; vide = tous.
 * @param depuis  visite précédente (ISO) ; null = premier passage, rien de « nouveau ».
 * @param suivis  problèmes suivis (Lot 12) : gardés même hors des métiers.
 */
export function evenementsAccueil(
  evenements: readonly Commentaire[],
  issues: readonly Issue[],
  metiers: readonly ClePerimetre[],
  depuis: string | null,
  suivis: ReadonlySet<string> = new Set(),
): EvenementAccueil[] {
  const parId = new Map(issues.map((i) => [i.id, i]))
  const seuil = depuis ? ms(depuis) : Number.POSITIVE_INFINITY
  return evenements
    .flatMap((e) => {
      const issue = parId.get(e.problemId)
      if (!issue) return []
      if (!dansPerimetre(issue, metiers) && !suivis.has(issue.id)) return []
      return [{ evenement: e, issue, nouveau: ms(e.date) > seuil }]
    })
    .sort((a, b) => ms(b.evenement.date) - ms(a.evenement.date))
}

/**
 * « Du nouveau sur vos déclarations » : ce que d'AUTRES ont fait sur mes
 * problèmes — ceux que j'ai déclarés et ceux que je suis (Lot 12).
 * Premier passage (pas de visite précédente) : les 7 derniers jours.
 */
export function nouveautesDeclarant(
  evenements: readonly EvenementAccueil[],
  mesIds: ReadonlySet<string>,
  moi: string,
  depuis: string | null,
  maintenant: number,
): EvenementAccueil[] {
  const seuil = depuis ? ms(depuis) : maintenant - JOURS_SANS_ACTIVITE * JOUR_MS
  const moiCle = moi.trim().toLowerCase()
  return evenements.filter(
    (e) =>
      mesIds.has(e.issue.id) &&
      e.evenement.auteur.trim().toLowerCase() !== moiCle &&
      ms(e.evenement.date) > seuil,
  )
}

/**
 * Par problème, le DERNIER événement d'un autre que moi depuis la visite
 * précédente (Lot 12 ter : pastille « Nouveau » de la carte des éléments
 * suivis). `evenements` est trié du plus récent au plus ancien : le premier
 * rencontré par problème est le bon.
 */
export function nouveautesParProbleme(
  evenements: readonly EvenementAccueil[],
  ids: ReadonlySet<string>,
  moi: string,
): Map<string, EvenementAccueil> {
  const moiCle = moi.trim().toLowerCase()
  const parProbleme = new Map<string, EvenementAccueil>()
  for (const e of evenements) {
    if (!e.nouveau || !ids.has(e.issue.id) || parProbleme.has(e.issue.id)) continue
    if (e.evenement.auteur.trim().toLowerCase() === moiCle) continue
    parProbleme.set(e.issue.id, e)
  }
  return parProbleme
}

/** Verbe affiché : « Karim Benali a commenté P2026-0041 ». */
export function verbeEvenement(e: Commentaire, estMoi: boolean): string {
  const v = (vous: string, il: string): string => (estMoi ? vous : il)
  switch (e.type) {
    case 'Creation': return v('avez déclaré', 'a déclaré')
    case 'Commentaire': {
      // Fichiers ajoutés sans message, photo / PDF annoté (27/10) : le verbe le dit.
      const auto = natureAutomatique(e.contenu)
      if (auto?.genre === 'fichiers') {
        const quoi = auto.nombre > 1 ? `${auto.nombre} fichiers` : 'un fichier'
        return v(`avez ajouté ${quoi}`, `a ajouté ${quoi}`)
      }
      if (auto?.genre === 'annotation') {
        const quoi = auto.quoi === 'pdf' ? 'un PDF' : 'une photo'
        return v(`avez annoté ${quoi}`, `a annoté ${quoi}`)
      }
      return v('avez commenté', 'a commenté')
    }
    case 'Qualification': return v('avez qualifié', 'a qualifié')
    case 'Cloture': return v('avez clos', 'a clos')
    case 'Annulation': return v('avez annulé', 'a annulé')
    case 'Modification':
      // Envoi au BE (22/10) : un geste à part, pas une donnée corrigée.
      return lireEvenementEnvoi(e.contenu) ? v('avez envoyé', 'a envoyé') : v('avez modifié', 'a modifié')
    case 'ChangementStatut':
      return e.nouveauStatut && e.ancienStatut && ['Clos', 'Annulé'].includes(e.ancienStatut)
        ? v('avez rouvert', 'a rouvert')
        : v('avez changé le statut de', 'a changé le statut de')
    default: {
      const inconnu: never = e.type
      return String(inconnu)
    }
  }
}

/**
 * Extrait affiché sous l'événement. La création n'a pas de texte utile
 * (« Problème déclaré par… ») : on montre le titre du problème.
 */
export function extraitEvenement(e: Commentaire, issue: Issue): string {
  if (e.type === 'Creation') return issue.titre
  // « Fichier ajouté » : déjà dit par le verbe (27/10).
  if (e.type === 'Commentaire' && natureAutomatique(e.contenu)) return ''
  // NC-S (06/10) : le libellé seul, pas le nom du fichier ni son adresse.
  if (e.type === 'Modification' && lireEvenementNcs(e.contenu)) return LIBELLE_EVENEMENT_NCS
  // Envoi au BE : à qui et le mot du coordinateur (« Au BE MECA · « Plan à reprendre » »).
  const envoi = e.type === 'Modification' ? resumeEvenementEnvoi(e.contenu) : null
  if (envoi) return envoi
  return e.contenu.replace(/\s+/g, ' ').trim()
}

// Recherche globale : déplacée dans `utils/rechercheGlobale` (05/10).

// ─── Listes simples ───

/** Les problèmes déclarés les plus récents (annulés exclus). */
export function derniersDeclares(issues: readonly Issue[], n: number): Issue[] {
  return issues
    .filter(compteCommeProbleme)
    .sort((a, b) => ms(b.dateDeclaration) - ms(a.dateDeclaration))
    .slice(0, n)
}

/** Mes déclarations : actives d'abord, puis les plus récentes. */
export function trierMesDeclarations(issues: readonly Issue[]): Issue[] {
  return [...issues].sort(
    (a, b) =>
      Number(estActif(b)) - Number(estActif(a)) ||
      ms(b.dateDeclaration) - ms(a.dateDeclaration),
  )
}

// ─── Regrouper par machine (02/10) ───
//
// File du coordinateur et activité récente deviennent vite un « bazar » quand
// plusieurs problèmes touchent la même machine : un bouton les range sous un
// titre par N° de série. Même règle que le tableau de « Tous les problèmes » :
// N° de série en MAJUSCULES, deux graphies d'une machine font un seul groupe.

export const SANS_MACHINE = 'Sans équipement'

export interface GroupeMachine<T> {
  /** N° de série en majuscules (ou SANS_MACHINE) : clé du groupe et titre affiché. */
  cle: string
  pn: string
  items: T[]
}

/** Un groupe par machine, dans l'ordre où chaque machine apparaît la première fois. */
export function grouperParMachine<T>(items: readonly T[], issueDe: (t: T) => Issue): GroupeMachine<T>[] {
  const groupes = new Map<string, GroupeMachine<T>>()
  for (const t of items) {
    const issue = issueDe(t)
    const cle = issue.numeroSerie.trim().toUpperCase() || SANS_MACHINE
    const g = groupes.get(cle)
    if (g) g.items.push(t)
    else groupes.set(cle, { cle, pn: issue.pn, items: [t] })
  }
  return [...groupes.values()]
}

/**
 * L'activité par machine : sous chaque machine, les événements d'un même
 * problème restent ensemble (le problème le plus récemment touché d'abord),
 * chacun du plus récent au plus ancien. Les machines dans l'ordre de leur
 * événement le plus récent.
 */
export function activiteParMachine(evenements: readonly EvenementAccueil[]): GroupeMachine<EvenementAccueil>[] {
  return grouperParMachine(evenements, (e) => e.issue).map((g) => {
    const rang = new Map<string, number>()
    g.items.forEach((e) => {
      if (!rang.has(e.issue.id)) rang.set(e.issue.id, rang.size)
    })
    const items = g.items
      .map((e, i) => ({ e, i }))
      .sort((a, b) => rang.get(a.e.issue.id)! - rang.get(b.e.issue.id)! || a.i - b.i)
      .map((x) => x.e)
    return { ...g, items }
  })
}

// ─── Un défaut, plusieurs machines (19/10, piste A de https://claude.ai/artifact/9X9nR8Vzzq8gEqjjwJz61p) ───

/** Écart maximal entre deux événements d'un même groupe pour n'en faire qu'une ligne. */
export const FENETRE_REGROUPEMENT_MS = 10 * 60 * 1000

export interface LigneFil {
  /** Les événements de la ligne, première fiche du groupe d'abord. Un seul = ligne ordinaire. */
  membres: EvenementAccueil[]
  /** Le plus récent : son auteur, sa date et sa place dans le fil. */
  principal: EvenementAccueil
  /** Au moins un membre est nouveau depuis la dernière visite. */
  nouveau: boolean
}

/**
 * Les événements IDENTIQUES des fiches sœurs d'un groupe (Lot 14) sur une
 * ligne : même groupe, même action, même auteur, même texte, à 10 min près.
 * C'est ce que produisent la déclaration sur plusieurs machines et les cases
 * « appliquer aussi aux autres machines ». Un événement propre à une fiche
 * (commentaire sur une seule machine, correction faite à un seul endroit)
 * garde sa ligne. Ordre du fil conservé (le plus récent d'abord).
 */
export function regrouperFil(evenements: readonly EvenementAccueil[]): LigneFil[] {
  const lignes: LigneFil[] = []
  const ouvertes = new Map<string, LigneFil>()
  for (const e of evenements) {
    const groupe = cleRapprochement(e.issue.groupe)
    const cle = groupe
      ? [groupe, e.evenement.type, cleRapprochement(e.evenement.auteur), extraitDuGroupe(e.evenement, e.issue)].join('|')
      : ''
    const ligne = cle ? ouvertes.get(cle) : undefined
    if (
      ligne &&
      !ligne.membres.some((m) => m.issue.id === e.issue.id) &&
      Math.abs(ms(ligne.principal.evenement.date) - ms(e.evenement.date)) <= FENETRE_REGROUPEMENT_MS
    ) {
      ligne.membres.push(e)
      ligne.nouveau ||= e.nouveau
      continue
    }
    const nouvelle: LigneFil = { membres: [e], principal: e, nouveau: e.nouveau }
    lignes.push(nouvelle)
    if (cle) ouvertes.set(cle, nouvelle)
  }
  for (const l of lignes) {
    if (l.membres.length > 1) l.membres.sort((a, b) => a.issue.id.localeCompare(b.issue.id, 'fr', { numeric: true }))
  }
  return lignes
}

/**
 * L'extrait d'une ligne qui regroupe des fiches sœurs : le renvoi « (publié
 * depuis P2026-0119) » des copies retiré (04/11) — il distinguait la copie de
 * l'original, et la ligne dit déjà « sur 2 équipements ».
 */
export function extraitDuGroupe(e: Commentaire, issue: Issue): string {
  return sansRenvoiGroupe(extraitEvenement(e, issue))
}

/** « a déclaré un défaut sur 2 machines », « a qualifié le même défaut sur 3 machines ». */
export function verbeGroupe(e: Commentaire, estMoi: boolean, machines: number): string {
  const verbe = verbeEvenement(e, estMoi)
  const sur = `sur ${libelleMachines(machines)}`
  if (e.type === 'Creation') return `${verbe} un défaut ${sur}`
  // « a changé le statut de » → « a changé le statut du même défaut ».
  if (verbe.endsWith(' de')) return `${verbe.slice(0, -3)} du même défaut ${sur}`
  return `${verbe} le même défaut ${sur}`
}
